import { Injectable, OnDestroy } from '@angular/core';
import { TreasuryService } from 'src/app/@pages/treasury/treasury.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { takeUntil } from 'rxjs/operators';
import { Subject, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ErrorsService } from '../../services/alerts/errors.service';
import { AlertsService } from '../../services/alerts/alerts.service';
import {
  ITransferDetails,
  ITransfers,
} from 'src/app/@interfaces/transfers.interface';

@Injectable({
  providedIn: 'root',
})
export class TransfersService implements OnDestroy {
  unsubscribe$ = new Subject<void>();
  constructor(
    private treasuryService: TreasuryService,
    private spinnerService: NgxSpinnerService,
    private router: Router,
    private errorsService: ErrorsService,
    private alertsService: AlertsService,
  ) {}

  async createSimpleTransfer(
    type: string,
    date: string,
    custom_value: number,
    id_payinvoice: number | null,
    original_payment: number,
    transferred_payment: number,
  ) {
    return new Promise<boolean>((resolve, reject) => {
      this.treasuryService
        .createTransfer(
          type,
          date,
          custom_value,
          id_payinvoice,
          original_payment,
          transferred_payment,
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (result) => {
            resolve(true);
          },
          (error) => {
            const errorTransformed = this.errorsService.getErrorFromJson(error);
            this.spinnerService.hide();
            this.alertsService
              .showErrorAlert(errorTransformed.message.split(':')[1].trim())
              .then((closed) => {
                if (closed.isConfirmed) {
                  reject(false);
                }
              });
          },
        );
    });
  }

  // PayInvoice.identifier ('AB'/'CA' + id_payinvoice) is generated once at
  // payment creation and is unique, so it doubles as an exact key for the
  // Transfer this payment's approval created — the transfer/origin/destination
  // data isn't part of getPaymentsInvoice and there's no id_payinvoice filter
  // on searchTranfers, only a substring search over identifier. That can
  // return unrelated rows whose identifier happens to contain this one as a
  // substring (e.g. "AB123" matches "AB1234"), so the exact-equality check
  // below is what actually selects the right transfer.
  async getTransferDetailsByIdentifier(
    identifier: string,
  ): Promise<ITransferDetails | null> {
    if (!identifier) {
      return null;
    }

    const result = await firstValueFrom(
      this.treasuryService.searchTranfers(identifier, 0, 50),
    );
    const transfer = (result?.transfers as ITransfers[] | undefined)?.find(
      (item) => item.invoice_payment?.identifier === identifier,
    );

    if (!transfer) {
      return null;
    }

    return {
      originLabel: transfer.original?.description ?? '',
      destinationLabel: transfer.transferred?.description ?? '',
    };
  }

  validSuccessfullTransfers(successfullTransfers: boolean[], refresh: boolean) {
    const allSuccessfull = successfullTransfers.every((item) => item === true);
    if (allSuccessfull) {
      this.alertsService
        .showSuccessfullAlert('Transfers Created')
        .then((result) => {
          if (result.isConfirmed && refresh) {
            this.router
              .navigateByUrl('/table', { skipLocationChange: true })
              .then(() => {
                this.router.navigate(['/treasury']);
              });
          }
        });
    } else {
      this.alertsService.showErrorAlert(
        'Some transfers may not have been created',
      );
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
